import React, { useState, useEffect, useRef, useContext } from 'react';
import { createChart, BaselineSeries, CandlestickSeries, CrosshairMode, HistogramSeries, LineStyle } from 'lightweight-charts';
import { StreamsContext } from '../../contexts/StreamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { useBarsFetcher } from '../../hooks/useTradeStationQueries';
import { getUnsupportedAssetClassBlock } from '../../utils/brokerConnection';
import { observeElementResize } from '../../utils/observeElementResize';
import ChartIntervalControl, { barsbackForInterval } from './ChartIntervalControl';

function mapBarTime(timestamp, unit) {
  if (!timestamp) return null;
  if (unit === 'Daily' || unit === 'Weekly' || unit === 'Monthly') {
    return String(timestamp).substring(0, 10);
  }
  const ms = Date.parse(timestamp);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

const PREVIEW_BAR_SPACING = 9;
const PREVIEW_RIGHT_OFFSET = 10;
const LINE_UP = '#26a69a';
const LINE_DOWN = '#8B7CFF';

/** Green above the quantity average, purple below. Baseline series splits the line and fill on that price. */
function quantityBaselineOptions(averagePrice) {
  const average = parseFloat(averagePrice);
  return {
    baseValue: {
      type: 'price',
      price: Number.isFinite(average) && average > 0 ? average : 0,
    },
    lineWidth: 2,
    priceLineVisible: false,
    topLineColor: LINE_UP,
    topFillColor1: `${LINE_UP}73`,
    topFillColor2: `${LINE_UP}10`,
    bottomLineColor: LINE_DOWN,
    bottomFillColor1: `${LINE_DOWN}10`,
    bottomFillColor2: `${LINE_DOWN}73`,
  };
}

/**
 * Self-contained lightweight-charts candlestick view for a single symbol.
 * Not wired into the multi-chart trading contexts (TradingContext/AppStateContext) —
 * intended for read-only previews (research pages, dashboard widgets) where a full
 * StockChart (order lines, brackets, drawing tools) would be overkill.
 */
const MiniStockChart = ({
  symbol,
  colors,
  height = 260,
  showEmptyState = true,
  fillHeight = false,
  hideHeader = false,
  interval: intervalProp,
  unit: unitProp,
  positionPrice = null,
  positionQuantity = null,
  exitLines = null,
  chartType = 'candle',
}) => {
  const { quotesBySymbol } = useContext(StreamsContext);
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const unsupportedAssetBlock = getUnsupportedAssetClassBlock(user, selectedAccount, symbol);
  const fetchBars = useBarsFetcher();
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const positionPriceLineRef = useRef(null);
  const exitPriceLinesRef = useRef([]);
  const candlesRef = useRef([]);
  const chartTypeRef = useRef(chartType);
  chartTypeRef.current = chartType;
  const positionPriceRef = useRef(positionPrice);
  positionPriceRef.current = positionPrice;
  const positionQuantityRef = useRef(positionQuantity);
  positionQuantityRef.current = positionQuantity;
  const quantityLineColorRef = useRef(colors.primary);
  quantityLineColorRef.current = colors.primary;
  const isLine = chartType === 'line';

  const syncQuantityLine = (series) => {
    if (!series) return;
    const averagePrice = parseFloat(positionPriceRef.current);
    const wantsLine = Number.isFinite(averagePrice) && averagePrice > 0;
    const existingLine = positionPriceLineRef.current;
    if (!wantsLine) {
      if (existingLine) {
        try { series.removePriceLine(existingLine); } catch (_) {}
        positionPriceLineRef.current = null;
      }
      return;
    }
    const qty = Math.abs(parseFloat(positionQuantityRef.current) || 0);
    const options = {
      price: averagePrice,
      color: quantityLineColorRef.current,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: qty > 0 ? `Qty: ${qty}` : 'AVG',
    };
    try {
      if (existingLine) {
        existingLine.applyOptions(options);
      } else {
        positionPriceLineRef.current = series.createPriceLine(options);
      }
    } catch (_) {
      positionPriceLineRef.current = null;
    }
  };
  // Tracks which symbol the candles currently belong to — prevents quote updates from
  // painting the new ticker's Last onto the previous ticker's last candle mid-switch.
  const loadedSymbolRef = useRef(null);
  const [intervalChoice, setIntervalChoice] = useState({ interval: 1, unit: 'Daily' });
  const [loading, setLoading] = useState(() => Boolean(symbol));
  // Series identity, not just the ref, so bars reload whenever the chart is rebuilt.
  const [chartSeries, setChartSeries] = useState(null);
  const [chartReady, setChartReady] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const chartHeight = fillHeight ? containerRef.current.clientHeight : height;
    const chart = createChart(containerRef.current, {
      layout: { background: { color: colors.bgCard }, textColor: colors.textMuted, fontFamily: 'inherit' },
      grid: { vertLines: { color: `${colors.border}80` }, horzLines: { color: `${colors.border}50` } },
      rightPriceScale: {
        borderColor: colors.border,
        scaleMargins: { top: 0.08, bottom: 0.22 },
      },
      timeScale: {
        borderColor: colors.border,
        timeVisible: true,
        secondsVisible: false,
        barSpacing: PREVIEW_BAR_SPACING,
        rightOffset: PREVIEW_RIGHT_OFFSET,
      },
      // Normal, not Magnet: the crosshair follows the pointer instead of snapping to close.
      crosshair: { mode: CrosshairMode.Normal },
      width: containerRef.current.clientWidth,
      height: chartHeight,
      handleScroll: true,
      handleScale: true,
    });
    chartRef.current = chart;
    setChartReady((n) => n + 1);
    const disconnectResize = observeElementResize(containerRef.current, () => {
      const live = chartRef.current;
      if (!containerRef.current || !live) return;
      try {
        live.applyOptions({
          width: containerRef.current.clientWidth,
          ...(fillHeight ? { height: containerRef.current.clientHeight } : {}),
        });
      } catch (_) {}
    });
    return () => {
      disconnectResize();
      chartRef.current = null;
      seriesRef.current = null;
      volumeSeriesRef.current = null;
      positionPriceLineRef.current = null;
      exitPriceLinesRef.current = [];
      setChartSeries(null);
      // Drop the chart after the current paint frame so a queued draw
      // does not hit a disposed canvas ("Object is disposed").
      requestAnimationFrame(() => {
        try { chart.remove(); } catch (_) {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors.bgCard, colors.border, colors.textMuted, height, fillHeight]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return undefined;

    if (seriesRef.current) {
      try { chart.removeSeries(seriesRef.current); } catch (_) {}
      seriesRef.current = null;
    }
    if (volumeSeriesRef.current) {
      try { chart.removeSeries(volumeSeriesRef.current); } catch (_) {}
      volumeSeriesRef.current = null;
    }
    positionPriceLineRef.current = null;
    exitPriceLinesRef.current = [];

    try {
      chart.priceScale('right').applyOptions({
        scaleMargins: { top: 0.08, bottom: 0.22 },
      });
    } catch (_) {}

    const series = isLine
      ? chart.addSeries(BaselineSeries, quantityBaselineOptions(positionPriceRef.current))
      : chart.addSeries(CandlestickSeries, {
          upColor: colors.success, downColor: colors.danger,
          borderUpColor: colors.success, borderDownColor: colors.danger,
          wickUpColor: colors.success, wickDownColor: colors.danger,
        });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: '',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    try {
      volumeSeries?.priceScale().applyOptions({
        scaleMargins: { top: 0.8, bottom: 0 },
      });
    } catch (_) {}
    seriesRef.current = series;
    volumeSeriesRef.current = volumeSeries;
    syncQuantityLine(series);
    setChartSeries(series);

    return () => {
      if (chartRef.current !== chart) return;
      try { chart.removeSeries(series); } catch (_) {}
      if (volumeSeries) {
        try { chart.removeSeries(volumeSeries); } catch (_) {}
      }
      if (seriesRef.current === series) seriesRef.current = null;
      if (volumeSeriesRef.current === volumeSeries) volumeSeriesRef.current = null;
    };
  }, [chartReady, isLine, colors.chartUp, colors.success, colors.danger]);

  /**
   * Position line lives outside the chart-creation effect: a fill changes AveragePrice
   * and Quantity, and rebuilding the chart there would drop the loaded bars, leaving
   * the chart empty until a quote painted a single candle. Updating in place also keeps
   * the user's pan / zoom.
   */
  useEffect(() => {
    if (!chartSeries) return;
    syncQuantityLine(chartSeries);
  }, [chartSeries, positionPrice, positionQuantity, colors.primary]);

  useEffect(() => {
    if (!chartSeries) return;
    exitPriceLinesRef.current.forEach((line) => {
      try { chartSeries.removePriceLine(line); } catch (_) {}
    });
    const next = (exitLines || [])
      .map((line) => ({ ...line, price: parseFloat(line.price) }))
      .filter((line) => Number.isFinite(line.price) && line.price > 0);
    exitPriceLinesRef.current = next.map((line) => chartSeries.createPriceLine({
      price: line.price,
      color: line.color || colors.danger,
      lineWidth: 2,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: line.title || '',
    }));
    return () => {
      exitPriceLinesRef.current.forEach((line) => {
        try { chartSeries.removePriceLine(line); } catch (_) {}
      });
      exitPriceLinesRef.current = [];
    };
  }, [chartSeries, exitLines, colors.danger]);

  useEffect(() => {
    if (!symbol || !chartSeries) return;
    if (unsupportedAssetBlock) {
      loadedSymbolRef.current = null;
      candlesRef.current = [];
      seriesRef.current.setData([]);
      volumeSeriesRef.current?.setData([]);
      setLoading(false);
      return;
    }
    const cfg = intervalProp != null
      ? {
          interval: intervalProp,
          unit: unitProp || 'Minute',
          barsback: barsbackForInterval(intervalProp, unitProp || 'Minute'),
        }
      : {
          interval: intervalChoice.interval,
          unit: intervalChoice.unit,
          barsback: barsbackForInterval(intervalChoice.interval, intervalChoice.unit),
        };
    let cancelled = false;
    // Clear immediately so live quotes can't stretch the previous symbol's last bar
    // into a temporary giant candle while the new bars are fetching.
    loadedSymbolRef.current = null;
    candlesRef.current = [];
    const series = chartSeries;
    try {
      series.setData([]);
      volumeSeriesRef.current?.setData([]);
    } catch (_) {
      return undefined;
    }
    setLoading(true);

    const applyBars = (data, { fit = true } = {}) => {
      if (cancelled || seriesRef.current !== series) return;
      const bars = (data?.Bars || [])
        .map((b) => {
          const time = mapBarTime(b.TimeStamp, cfg.unit);
          const open = parseFloat(b.Open);
          const high = parseFloat(b.High);
          const low = parseFloat(b.Low);
          const close = parseFloat(b.Close);
          if (time == null || [open, high, low, close].some((n) => !Number.isFinite(n))) return null;
          return { time, open, high, low, close };
        })
        .filter(Boolean)
        .sort((a, b) => (a.time < b.time ? -1 : 1));
      const volumeBars = (data?.Bars || [])
        .map((b) => {
          const time = mapBarTime(b.TimeStamp, cfg.unit);
          const open = parseFloat(b.Open);
          const close = parseFloat(b.Close);
          if (time == null || !Number.isFinite(open) || !Number.isFinite(close)) return null;
          return {
            time,
            value: Math.max(0, parseFloat(b.TotalVolume || b.Volume || 0)),
            color: close >= open ? `${colors.success}80` : `${colors.danger}80`,
          };
        })
        .filter(Boolean)
        .sort((a, b) => (a.time < b.time ? -1 : 1));
      candlesRef.current = bars;
      loadedSymbolRef.current = symbol;
      try {
        if (chartTypeRef.current === 'line') {
          series.applyOptions(quantityBaselineOptions(positionPriceRef.current));
          series.setData(bars.map((bar) => ({ time: bar.time, value: bar.close })));
          syncQuantityLine(series);
        } else {
          series.setData(bars);
          syncQuantityLine(series);
        }
        volumeSeriesRef.current?.setData(volumeBars);
        if (fit && chartRef.current) {
          chartRef.current.timeScale().fitContent();
          chartRef.current.timeScale().applyOptions({
            barSpacing: PREVIEW_BAR_SPACING,
            rightOffset: PREVIEW_RIGHT_OFFSET,
          });
        }
      } catch (_) {}
    };

    const request = { ticker: symbol, interval: cfg.interval, unit: cfg.unit, barsback: cfg.barsback, sessionTemplate: 'USEQPreAndPost' };
    fetchBars(request)
      .then((data) => {
        applyBars(data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => {
      cancelled = true;
    };
  }, [symbol, intervalChoice, intervalProp, unitProp, fetchBars, colors.success, colors.danger, unsupportedAssetBlock, chartSeries]);

  useEffect(() => {
    if (loadedSymbolRef.current !== symbol) return;
    const quote = quotesBySymbol?.[symbol];
    if (!quote || !seriesRef.current || !candlesRef.current.length) return;
    const last = parseFloat(quote.Last);
    if (isNaN(last)) return;
    const prev = candlesRef.current[candlesRef.current.length - 1];
    const next = {
      time: prev.time,
      open: prev.open,
      high: Math.max(prev.high, last),
      low: Math.min(prev.low, last),
      close: last,
    };
    candlesRef.current[candlesRef.current.length - 1] = next;
    try {
      if (chartTypeRef.current === 'line') {
        seriesRef.current.update({ time: prev.time, value: last });
      } else {
        seriesRef.current.update(next);
      }
    } catch (_) {}
  }, [quotesBySymbol, symbol]);

  useEffect(() => {
    if (chartType !== 'line' || !seriesRef.current) return;
    try {
      seriesRef.current.applyOptions(quantityBaselineOptions(positionPrice));
    } catch (_) {}
  }, [chartType, positionPrice, chartSeries]);

  return (
    <div style={{ width: '100%', ...(fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}) }}>
      {!hideHeader && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '8px 12px 4px', flexShrink: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginRight: 'auto' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
            {loading ? 'Loading…' : (symbol || '')}
          </span>
        </span>
        <ChartIntervalControl
          interval={intervalChoice.interval}
          unit={intervalChoice.unit}
          setIntervalAndUnit={(nextInterval, nextUnit) => setIntervalChoice({ interval: nextInterval, unit: nextUnit })}
          showSessionToggle={false}
        />
      </div>
      )}
      <div style={{
        position: 'relative',
        width: '100%',
        ...(fillHeight ? { flex: 1, minHeight: 0 } : { height }),
      }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        {unsupportedAssetBlock && symbol ? (
          <div
            data-testid="mini-stock-chart-unsupported"
            role="status"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              textAlign: 'center',
              color: colors.text,
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.45,
              background: colors.bgCard || colors.bgPrimary || '#111',
            }}
          >
            {unsupportedAssetBlock.message}
          </div>
        ) : loading && symbol ? (
          <div
            data-testid="mini-stock-chart-loading"
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.text,
              fontSize: 13,
              fontWeight: 600,
              background: colors.bgCard || colors.bgPrimary || '#111',
            }}
          >
            Loading chart
          </div>
        ) : null}
        {!symbol && showEmptyState && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.textMuted,
              fontSize: 13,
              background: colors.bgCard,
            }}
          >
            Select a symbol to preview its chart
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniStockChart;
